import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { withAreaPrefix } from '../area-routes';

const rawApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

/** Backend serves at /api/v1/...; normalize so baseURL always has /api/v1. */
function getApiBaseUrl(): string {
  let base = rawApiUrl.trim().replace(/\/+$/, '');
  const currentPath = globalThis.location?.pathname ?? '/';
  const isAdminArea = currentPath === '/admin' || currentPath.startsWith('/admin/');
  if (base.endsWith('/api')) base = `${base}/v1`;
  if (isAdminArea && !base.endsWith('/admin')) base = `${base}/admin`;
  return base;
}

const API_URL = getApiBaseUrl();

/** Request timeout in ms (used by apiClient and by refresh request to avoid hanging). */
const REQUEST_TIMEOUT_MS = 30000;

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT_MS,
});

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

type FailedRequest = {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  config: InternalAxiosRequestConfig;
};

let failedQueue: FailedRequest[] = [];

const processQueue = (error: Error | null, token: string | null = null): void => {
  failedQueue.forEach(request => {
    if (error) {
      request.reject(error);
    } else if (token) {
      request.config.headers.Authorization = `Bearer ${token}`;
      request.resolve(apiClient(request.config));
    }
  });
  failedQueue = [];
};

apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    throw error;
  }
);

/** Backend success envelope: { code, message, data } */
const API_SUCCESS_CODE = 'SUCCESS';

/**
 * Error response từ backend: { code, message, path, statusCode }.
 * Sau normalizeError có thêm messageKey cho i18n.
 */
interface ErrorResponseData {
  code?: string;
  message?: string;
  path?: string;
  statusCode?: number;
  messageKey?: string;
}

/**
 * Normalize error to ensure it has code and messageKey
 */
const normalizeError = (error: AxiosError): AxiosError => {
  // Handle network errors (no response received)
  if (!error.response) {
    // Check if it's a timeout error
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      // Create normalized error response for timeout
      const networkError = {
        ...error,
        response: {
          ...(error.response && typeof error.response === 'object' ? error.response : {}),
          data: {
            code: 'NETWORK_ERROR',
            message: 'Request timeout. Please check your connection and try again.',
            path: error.config?.url ?? '',
            statusCode: 0,
            messageKey: 'auth.errors.network_error',
          } as ErrorResponseData,
          status: 0,
          statusText: 'Network Error',
          headers: {},
          config: error.config,
        },
      } as AxiosError;
      return networkError;
    }

    // Handle connection errors
    if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      const networkError = {
        ...error,
        response: {
          ...(error.response && typeof error.response === 'object' ? error.response : {}),
          data: {
            code: 'NETWORK_ERROR',
            message: 'Network connection failed. Please check your internet connection.',
            path: error.config?.url ?? '',
            statusCode: 0,
            messageKey: 'auth.errors.network_error',
          } as ErrorResponseData,
          status: 0,
          statusText: 'Network Error',
          headers: {},
          config: error.config,
        },
      } as AxiosError;
      return networkError;
    }

    // Fallback for other network errors
    const networkError = {
      ...error,
      response: {
        ...(error.response && typeof error.response === 'object' ? error.response : {}),
        data: {
          code: 'NETWORK_ERROR',
          message: 'Network error occurred. Please try again.',
          path: error.config?.url ?? '',
          statusCode: 0,
          messageKey: 'auth.errors.network_error',
        } as ErrorResponseData,
        status: 0,
        statusText: 'Network Error',
        headers: {},
        config: error.config,
      },
    } as AxiosError;
    return networkError;
  }

  // Backend trả lỗi chỉ { code, message }; thêm statusCode + messageKey cho frontend i18n
  if (error.response?.data) {
    const errorData = error.response.data as ErrorResponseData;
    const code = errorData.code || 'UNKNOWN_ERROR';
    const normalizedData: ErrorResponseData = {
      code,
      message: errorData.message || error.message || 'An error occurred',
      path: errorData.path ?? error.config?.url ?? '',
      statusCode: errorData.statusCode ?? error.response.status,
      messageKey: errorData.messageKey ?? `auth.errors.${code.toLowerCase()}`,
    };
    error.response.data = normalizedData;
  }

  return error;
};

apiClient.interceptors.response.use(
  response => {
    /** Unwrap backend envelope { code, message, data } -> response.data = data */
    const body = response.data as { code?: string; message?: string; data?: unknown };
    if (body?.code === API_SUCCESS_CODE && body != null && 'data' in body) {
      response.data = body.data;
    }
    return response;
  },
  async (error: AxiosError) => {
    // Normalize error to ensure it has code and messageKey
    const normalizedError = normalizeError(error);

    const originalRequest = normalizedError.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Preserve error format - check if it's a 401 that needs token refresh
    if (normalizedError.response?.status !== 401 || originalRequest._retry) {
      // Error format already preserved, throw as-is
      throw normalizedError;
    }

    // Don't retry auth endpoints
    if (
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/register') ||
      originalRequest.url?.includes('/auth/refresh')
    ) {
      // Preserve error format when throwing
      throw normalizedError;
    }

    // Handle token refresh queue
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('ffshop_refresh_token');

    if (!refreshToken) {
      isRefreshing = false;
      // Preserve error format when processing queue
      processQueue(normalizedError as unknown as Error, null);
      redirectToLogin();
      throw normalizedError;
    }

    refreshPromise = (async () => {
      try {
        const response = await axios.post(
          `${API_URL}/auth/refresh`,
          { refreshToken },
          { timeout: REQUEST_TIMEOUT_MS }
        );
        const raw = response.data as
          | { accessToken: string; refreshToken: string; expiresIn: number }
          | {
              code: string;
              message: string;
              data: { accessToken: string; refreshToken: string; expiresIn: number };
            };
        const payload = raw && 'data' in raw ? raw.data : raw;
        const { accessToken, refreshToken: newRefresh, expiresIn } = payload;

        const expiresAt = Date.now() + expiresIn * 1000;
        localStorage.setItem('ffshop_access_token', accessToken);
        localStorage.setItem('ffshop_refresh_token', newRefresh);
        localStorage.setItem('ffshop_token_expires', expiresAt.toString());
        localStorage.setItem('token', accessToken);

        processQueue(null, accessToken);
        return true;
      } catch (refreshError) {
        localStorage.removeItem('ffshop_access_token');
        localStorage.removeItem('ffshop_refresh_token');
        localStorage.removeItem('ffshop_token_expires');
        localStorage.removeItem('token');

        // Normalize refresh error to preserve format
        const normalizedRefreshError = normalizeError(refreshError as AxiosError);
        processQueue(normalizedRefreshError as unknown as Error, null);
        return false;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();

    try {
      const refreshed = await refreshPromise;
      if (refreshed) {
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
        return apiClient(originalRequest);
      }
    } catch {
      // ignore, handled below
    }

    redirectToLogin();
    // Preserve original error format when throwing
    throw normalizedError;
  }
);

function redirectToLogin(): void {
  if (!globalThis.location.pathname.includes('/auth/')) {
    globalThis.location.href = withAreaPrefix('/auth/login', globalThis.location.pathname);
  }
}

export default apiClient;
